import { unwrapResult } from '@reduxjs/toolkit';
import React, { useCallback, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import styled from 'styled-components';
import { useAppDispatch } from '../..';
import Button from '../../components/Button';
import FlashMessage from '../../components/FlashMessage';
import { FullPageLoader } from '../../components/Loading';
import MarketingPreferencesCard from '../../components/MarketingPreferences';
import PageTitle from '../../components/PageTitle';
import { P1 } from '../../components/Typography';
import {
  fetchMarketingPreferencesThunk,
  UPDATE_MARKETING_PREFERENCES_STORE_KEY,
  updateMarketingPreferencesThunk,
} from '../../redux/user/user.thunk';
import { MarketingPreferenceChoice, UNTICKED_MARKETING_PREFERENCES } from '../../utils/marketing-preferences';
import { FLASH_MESSAGE_TIMOUT, useFlashMessage } from '../FlashMessage';

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Preferences: React.FC = () => {
  const dispatch = useAppDispatch();
  const [isPreferencesLoaded, setIsPreferencesLoaded] = useState(false);
  const [choice, setChoice] = useState<MarketingPreferenceChoice>(UNTICKED_MARKETING_PREFERENCES);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [flashMessagePayload, dismissFlashMessage] = useFlashMessage([UPDATE_MARKETING_PREFERENCES_STORE_KEY]);

  const loadPreferences = useCallback(
    () =>
      dispatch(fetchMarketingPreferencesThunk())
        .then(unwrapResult)
        .then(({ emailOptIn, smsOptIn }) => {
          setChoice({ emailOptIn, smsOptIn });
          setIsPreferencesLoaded(true);
        }),
    [dispatch]
  );

  useEffect(() => {
    loadPreferences().catch(() => setLoadFailed(true));
  }, [loadPreferences]);

  const save = () => {
    if (!isPreferencesLoaded) return;
    setIsSaving(true);
    dispatch(updateMarketingPreferencesThunk(choice))
      .then(unwrapResult)
      .then(({ emailOptIn, smsOptIn }) => {
        setChoice({ emailOptIn, smsOptIn });
      })
      .catch(() => undefined)
      .then(() => setIsSaving(false));
  };

  if (!isPreferencesLoaded && !loadFailed) return <FullPageLoader />;

  return (
    <Container>
      <Row>
        <Col lg={10} push={{ lg: 1 }}>
          <PageTitle>Your preferences</PageTitle>
        </Col>
      </Row>
      {flashMessagePayload ? (
        <Row>
          <Col lg={10} push={{ lg: 1 }}>
            <FlashMessage payload={flashMessagePayload} onDismiss={dismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMOUT} />
          </Col>
        </Row>
      ) : null}
      <Row>
        <Col lg={10} push={{ lg: 1 }}>
          {isPreferencesLoaded ? (
            <MarketingPreferencesCard value={choice} onChange={setChoice}>
              <Actions>
                <Button buttonType="primary" onClick={save} disabled={isSaving}>
                  Save preferences
                </Button>
              </Actions>
            </MarketingPreferencesCard>
          ) : (
            <P1>We couldn't load your preferences. Please refresh the page to try again.</P1>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default Preferences;
